import { isDev } from '../../core/utils/is-node-environment.js';
import { segmentsClient } from '../segment-selector/contexts/SegmentsCache/SegmentsCache.js';

/**
 * Helper function to update useQueries cache optimistically.
 * In the current implementation it only resets the state for one specified query.
 * @internal
 *
 * @param queryKey - affected query.
 * @param updateCacheFn - function that updates the cache to the expected outcome.
 * @returns previous state, in case the mutation needs to be rolled back.
 */
export async function runOptimisticMutation<T>(
  queryKey: string,
  updateCacheFn: (prevState: T) => T,
  cancelKeys?: string[],
): Promise<{ previousState?: T }> {
  // Cancel any outgoing refetches
  // (so they don't overwrite our optimistic update)
  await segmentsClient.queryClient.cancelQueries({
    queryKey: cancelKeys ?? [queryKey],
  });

  // Snapshot the previous value
  const previousState = segmentsClient.queryClient.getQueryData<T>([queryKey]);

  // Optimistically update to the new value
  segmentsClient.queryClient.setQueryData([queryKey], updateCacheFn);

  // Return a context object with the snapshotted value
  return { previousState };
}

/**
 * Helper function to react to errors of an optimistically run mutation.
 * @internal
 *
 * @param queryKey - affected query.
 * @param error - API response error.
 * @param context - object containing the previousState. It is usually derived from the return type of `runOptimisticMutation`.
 */
export async function handleFailedOptimisticMutation<T>(
  queryKey: string,
  error: Error,
  context?: { previousState?: T },
) {
  if (isDev()) {
    // oxlint-disable-next-line no-console
    console.warn(
      `Couldn't run a mutation that manipulates the ${queryKey}-query.`,
      error,
    );
  }

  // use the context returned from onMutate to roll back
  segmentsClient.queryClient.setQueryData([queryKey], context?.previousState);
}

/**
 * Helper function to react to a successful optimistical mutation.
 * @internal
 *
 * @param invalidQueryKeys - queries that have been optimistically updated and can now be refetched.
 * @param mutationKeys - mutation keys modifiying the same data, to prevent overwrites.
 */
export async function handleSettledOptimisticMutation(
  invalidQueryKeys: string[],
  mutationKeys: string[],
) {
  if (
    // We only want to invalidate the query if there is no ongoing mutation other then the current one.
    segmentsClient.queryClient.isMutating({
      mutationKey: mutationKeys,
    }) === 1
  ) {
    await segmentsClient.queryClient.invalidateQueries({
      queryKey: invalidQueryKeys,
    });
  }
}
